/**
 * Traços de desenho de cada expressão do domo.
 *
 * Isto é a transcrição fiel do `static STATES` do app legado
 * (docs/legacy-app-reference.html, linha 709) — mas só a parte que descreve
 * *como o rosto é desenhado*. Chave, rótulo, cor e textos NÃO moram aqui:
 * vêm de `DOME[state]` em @will/contracts, que é a fonte única. Duplicar cor
 * ou label neste arquivo seria recriar a divergência que o ADR-009 fechou.
 *
 * Campos do legado deliberadamente não transcritos:
 *  - `face`: as 14 expressões usam 'eyes'; os modos 'think'/'orb'/'smile' eram
 *    caminhos mortos do template.
 *  - `brow.flat` e `squint`: existiam no dado mas nenhum ramo do renderizador
 *    os lia — o "achatado" de `failed` e `resting` já está nos ângulos l/r.
 *  - `heart`: a linha de ECG do descanso era desenhada fora do rosto, na
 *    coluna de texto. É do painel, não do domo.
 */

import type { DomeState } from '@will/contracts';

/** Sobrancelha: ângulos em graus por lado, deslocamento vertical em unidades do SVG. */
export interface BrowShape {
  /** rotação da sobrancelha esquerda, em graus */
  l: number;
  /** rotação da sobrancelha direita, em graus */
  r: number;
  /** deslocamento vertical comum às duas */
  y: number;
  /** ergue só a esquerda — a assimetria de quem está em dúvida */
  lift?: true;
  /** franzido inquieto, com pulso próprio e mais rápido */
  fret?: true;
}

export interface FaceTraits {
  brow: BrowShape;
  /** grafo de nós acima dos olhos — raciocínio em curso */
  nodes?: true;
  /** trilho de varredura sob os olhos */
  sweep?: true;
  /** olhar que sobe e vagueia, de quem procura a resposta */
  ponder?: true;
  /** arco escuro na base do olho — olho sorridente */
  arcDown?: true;
  /** arco escuro no topo do olho — olho satisfeito */
  arcUp?: true;
  /** linhas de velocidade atravessando os olhos */
  streak?: true;
  /** varredura de linhas dentro do olho */
  scan?: true;
  /** anéis girando em volta do rosto */
  halo?: true;
  /** caractere do distintivo ao lado do rosto */
  badge?: string;
  /** sorriso discreto abaixo dos olhos */
  softSmile?: true;
  /** barras de boca — fala */
  mouth?: true;
  /** o rosto inteiro inclina */
  tilt?: true;
  /** o rosto balança de ombros */
  shrug?: true;
  /** boca caída */
  droop?: true;
  /** barras de inicialização */
  boot?: true;
  /** lágrima escorrendo */
  tears?: true;
  /** boca triste */
  sadMouth?: true;
  /** olhos quase fechados, respirando */
  sleepy?: true;
  /** zzz subindo */
  zzz?: true;
}

export const FACE: Record<DomeState, FaceTraits> = {
  listening: { brow: { l: -5, r: 5, y: 2 } },
  thinking: { brow: { l: -16, r: 4, y: -2, lift: true }, nodes: true, sweep: true, ponder: true },
  opportunity: { brow: { l: -16, r: 16, y: -3 }, arcDown: true },
  focus: { brow: { l: -7, r: 7, y: 1 }, scan: true },
  busy: { brow: { l: -3, r: 3, y: 3 }, streak: true, halo: true },
  worried: { brow: { l: -13, r: 13, y: 0, fret: true }, badge: '!' },
  completed: { brow: { l: -11, r: 11, y: -4 }, softSmile: true, arcUp: true },
  speaking: { brow: { l: -6, r: 6, y: 1 }, mouth: true },
  awaiting: { brow: { l: -14, r: 6, y: -3, lift: true }, tilt: true, badge: '?' },
  unclear: { brow: { l: -10, r: 10, y: -4 }, shrug: true, badge: '?' },
  failed: { brow: { l: -14, r: 14, y: 6 }, droop: true, badge: '×' },
  waking: { brow: { l: -5, r: 5, y: 2 }, boot: true },
  sad: { brow: { l: -9, r: 9, y: 4 }, tears: true, sadMouth: true },
  resting: { brow: { l: 9, r: -9, y: 7 }, sleepy: true, zzz: true },
};
